import {
  deriveSeoReportDisplayModel,
  deriveSeoReportPublicationReadiness,
  getEffectiveSeoGeneratedArtifactStatus,
  getSeoReportStaleProcessingMessage,
  summarizeSeoPublicationReadiness,
  type PublishedResearchReport,
  type ResearchReportStatus,
  type SeoGeneratedComponentStatus,
  type SeoGeneratedComponents,
  type SeoReportDisplayStatus,
  type SeoReportPublicationGate,
  type SeoReportPublicationReadinessState,
} from '@dupe/common/research-report'

export const SEO_ADMIN_COMPONENT_TARGETS = [
  'image_check',
  'hero_image',
  'quick_answer',
  'who_its_for',
  'top_pick_descriptors',
  'product_quotes',
  'social_signals',
  'visual_signals',
  'how_we_picked',
  'faq',
] as const

const SUPPORTED_COMPONENT_REGENERATION_TARGETS =
  new Set<SeoAdminComponentTarget>([
    'image_check',
    'hero_image',
    'quick_answer',
    'who_its_for',
    'top_pick_descriptors',
    'product_quotes',
    'social_signals',
    'visual_signals',
    'how_we_picked',
    'faq',
  ])

export type SeoAdminComponentTarget =
  (typeof SEO_ADMIN_COMPONENT_TARGETS)[number]

export type SeoAdminActionTarget = 'report' | SeoAdminComponentTarget

export type SeoAdminComponentDisplayStatus =
  | 'idle'
  | 'processing'
  | 'ready'
  | 'failed'

export type SeoAdminPublicationGate = 'slug' | SeoReportPublicationGate

export type SeoAdminPublicationGateDisplayStatus =
  | 'ready'
  | 'upgrade'
  | 'processing'
  | 'failed'
  | 'missing'

const SEO_ADMIN_PUBLICATION_GATE_LABELS: Record<
  SeoAdminPublicationGate,
  string
> = {
  slug: 'Slug',
  document: 'Report document',
  category: 'Category',
  image_check: 'Image check',
  hero_image: 'Hero image',
  quick_answer: 'Quick answer',
  who_its_for: 'Who this is for',
  top_pick_descriptors: 'Top pick descriptors',
  product_quotes: 'Product quotes',
  how_we_picked: 'How we picked',
  faq: 'FAQ',
  seo_title: 'SEO title',
  meta_description: 'Meta description',
}

const SEO_ADMIN_PUBLICATION_GATES = [
  'slug',
  'document',
  'category',
  'seo_title',
  'meta_description',
  'image_check',
  'hero_image',
  'quick_answer',
  'who_its_for',
  'top_pick_descriptors',
  'product_quotes',
  'how_we_picked',
  'faq',
] as const satisfies SeoAdminPublicationGate[]

// social_signals and visual_signals are intentionally not publication gates.
// They are optional enrichment that operators can regenerate and monitor, but
// they should not block a report from going public.
interface GeneratedComponentRecord {
  status?: SeoGeneratedComponentStatus | null
  queuedAt?: string | null
  startedAt?: string | null
  completedAt?: string | null
  generatedAt?: string | null
  lastError?: string | null
}

export interface SeoAdminComponentState {
  target: SeoAdminComponentTarget
  rawStatus: SeoGeneratedComponentStatus | null
  displayStatus: SeoAdminComponentDisplayStatus
  isProcessing: boolean
  needsUpgrade: boolean
  canRegenerate: boolean
  message?: string | null
  generatedAt?: string | null
  lastError?: string | null
}

export interface SeoAdminSeoMetadataState {
  target: 'seo_metadata'
  rawStatus: SeoGeneratedComponentStatus | null
  displayStatus: SeoAdminComponentDisplayStatus
  isProcessing: boolean
  needsUpgrade: boolean
  canRegenerate: boolean
  message?: string | null
  generatedAt?: string | null
  lastError?: string | null
}

export interface SeoAdminPublicationGateState {
  gate: SeoAdminPublicationGate
  label: string
  displayStatus: SeoAdminPublicationGateDisplayStatus
  message?: string | null
}

export interface SeoAdminReportState {
  rawStatus: ResearchReportStatus
  displayStatus: SeoReportDisplayStatus
  publicationReadiness: SeoReportPublicationReadinessState
  publicationReadinessSummary: string
  isStale: boolean
  /** Logged problems still awaiting a decision (research_report_issues). */
  openIssueCount: number
  isLive: boolean
  slugReady: boolean
  isEnriching: boolean
  hasComponentFailures: boolean
  upgradeGates: SeoReportPublicationGate[]
  shouldPoll: boolean
  canPublish: boolean
  canUnpublish: boolean
  canRegenerateAll: boolean
  canRegenerateDocument: boolean
  canDelete: boolean
  primaryMessage?: string | null
  componentStates: Record<SeoAdminComponentTarget, SeoAdminComponentState>
  seoMetadataState: SeoAdminSeoMetadataState
  publicationGateStates: SeoAdminPublicationGateState[]
}

function getGeneratedComponentRecords(
  generatedComponents: SeoGeneratedComponents | null | undefined
): Record<SeoAdminComponentTarget, GeneratedComponentRecord | undefined> {
  return {
    image_check: generatedComponents?.image_check,
    hero_image: generatedComponents?.hero_image,
    quick_answer: generatedComponents?.quick_answer,
    who_its_for: generatedComponents?.who_its_for,
    top_pick_descriptors: generatedComponents?.top_pick_descriptors,
    product_quotes: generatedComponents?.product_quotes,
    social_signals: generatedComponents?.social_signals,
    visual_signals: generatedComponents?.visual_signals,
    how_we_picked: generatedComponents?.how_we_picked,
    faq: generatedComponents?.faq,
  }
}

function toComponentDisplayStatus(
  status: SeoGeneratedComponentStatus | null
): SeoAdminComponentDisplayStatus {
  if (status == null) return 'idle'
  if (status === 'queued' || status === 'generating') return 'processing'
  if (status === 'ready') return 'ready'
  return 'failed'
}

function getComponentMessage(args: {
  target?: SeoAdminComponentTarget
  rawStatus: SeoGeneratedComponentStatus | null
  displayStatus: SeoAdminComponentDisplayStatus
  needsUpgrade?: boolean
  missingCurrentPayload?: boolean
  lastError?: string | null
}): string | null {
  if (args.displayStatus === 'processing') {
    return args.rawStatus === 'queued' ? 'Queued' : 'Generating'
  }

  if (args.displayStatus === 'failed') {
    return args.lastError ?? 'Generation failed.'
  }

  if (
    args.target === 'visual_signals' &&
    args.displayStatus === 'ready' &&
    args.lastError
  ) {
    return `Ready with note: ${args.lastError}`
  }

  if (args.needsUpgrade) {
    return 'Upgrade available.'
  }

  if (args.missingCurrentPayload) {
    return 'Missing current component payload.'
  }

  return null
}

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function deriveComponentState(args: {
  target: SeoAdminComponentTarget
  component?: GeneratedComponentRecord
  reportDisplayStatus: SeoReportDisplayStatus
  needsUpgrade: boolean
  missingCurrentPayload: boolean
  updatedAt: string | Date | null | undefined
  now?: number
}): SeoAdminComponentState {
  const rawStatus = args.component?.status ?? null
  const effectiveStatus = getEffectiveSeoGeneratedArtifactStatus({
    artifact: args.component,
    fallbackUpdatedAt: args.updatedAt,
    now: args.now,
    staleWhenProcessingWithoutTimestamp: args.reportDisplayStatus === 'live',
  })
  const displayStatus = args.missingCurrentPayload
    ? 'idle'
    : toComponentDisplayStatus(effectiveStatus)
  const isProcessing = displayStatus === 'processing'

  return {
    target: args.target,
    rawStatus,
    displayStatus,
    isProcessing,
    needsUpgrade: args.needsUpgrade,
    canRegenerate:
      SUPPORTED_COMPONENT_REGENERATION_TARGETS.has(args.target) &&
      args.reportDisplayStatus !== 'processing' &&
      args.reportDisplayStatus !== 'archived' &&
      !isProcessing,
    message: getComponentMessage({
      target: args.target,
      rawStatus,
      displayStatus,
      needsUpgrade: args.needsUpgrade,
      missingCurrentPayload: args.missingCurrentPayload,
      lastError: args.component?.lastError ?? null,
    }),
    generatedAt: args.component?.generatedAt ?? null,
    lastError: args.component?.lastError ?? null,
  }
}

function deriveSeoMetadataState(args: {
  generatedComponents: SeoGeneratedComponents | null | undefined
  reportDisplayStatus: SeoReportDisplayStatus
  upgradeGates: SeoReportPublicationGate[]
  updatedAt: string | Date | null | undefined
  now?: number
}): SeoAdminSeoMetadataState {
  const title = args.generatedComponents?.seo_title
  const description = args.generatedComponents?.meta_description
  const titleStatus = getEffectiveSeoGeneratedArtifactStatus({
    artifact: title,
    fallbackUpdatedAt: args.updatedAt,
    now: args.now,
    staleWhenProcessingWithoutTimestamp: args.reportDisplayStatus === 'live',
  })
  const descriptionStatus = getEffectiveSeoGeneratedArtifactStatus({
    artifact: description,
    fallbackUpdatedAt: args.updatedAt,
    now: args.now,
    staleWhenProcessingWithoutTimestamp: args.reportDisplayStatus === 'live',
  })
  const statuses = [titleStatus, descriptionStatus]
  const titleReady = titleStatus === 'ready' && hasText(title?.content?.text)
  const descriptionReady =
    descriptionStatus === 'ready' && hasText(description?.content?.text)
  const rawStatus =
    statuses.find((status) => status === 'generating') ??
    statuses.find((status) => status === 'queued') ??
    statuses.find((status) => status === 'failed') ??
    (titleReady && descriptionReady ? 'ready' : null)
  const displayStatus =
    titleReady && descriptionReady
      ? 'ready'
      : toComponentDisplayStatus(rawStatus)
  const isProcessing = displayStatus === 'processing'
  const lastError = title?.lastError ?? description?.lastError ?? null
  const needsUpgrade =
    args.upgradeGates.includes('seo_title') ||
    args.upgradeGates.includes('meta_description')

  return {
    target: 'seo_metadata',
    rawStatus,
    displayStatus,
    isProcessing,
    needsUpgrade,
    canRegenerate:
      args.reportDisplayStatus !== 'processing' &&
      args.reportDisplayStatus !== 'archived' &&
      !isProcessing,
    message: getComponentMessage({
      rawStatus,
      displayStatus,
      needsUpgrade,
      lastError: lastError ?? 'SEO metadata generation failed.',
    }),
    generatedAt: title?.generatedAt ?? description?.generatedAt ?? null,
    lastError,
  }
}

function derivePublicationGateStates(args: {
  slugReady: boolean
  processingGates: SeoReportPublicationGate[]
  failedGates: SeoReportPublicationGate[]
  missingGates: SeoReportPublicationGate[]
  upgradeGates: SeoReportPublicationGate[]
}): SeoAdminPublicationGateState[] {
  return SEO_ADMIN_PUBLICATION_GATES.map((gate) => {
    let displayStatus: SeoAdminPublicationGateDisplayStatus = 'ready'
    if (gate === 'slug' && !args.slugReady) {
      displayStatus = 'missing'
    } else if (gate !== 'slug' && args.processingGates.includes(gate)) {
      displayStatus = 'processing'
    } else if (gate !== 'slug' && args.failedGates.includes(gate)) {
      displayStatus = 'failed'
    } else if (gate !== 'slug' && args.missingGates.includes(gate)) {
      displayStatus = 'missing'
    } else if (gate !== 'slug' && args.upgradeGates.includes(gate)) {
      displayStatus = 'upgrade'
    }

    return {
      gate,
      label: SEO_ADMIN_PUBLICATION_GATE_LABELS[gate],
      displayStatus,
      message:
        displayStatus === 'ready'
          ? null
          : displayStatus === 'upgrade'
            ? `${SEO_ADMIN_PUBLICATION_GATE_LABELS[gate]} can be regenerated to the latest format.`
            : `${SEO_ADMIN_PUBLICATION_GATE_LABELS[gate]} is ${displayStatus}.`,
    }
  })
}

function getPrimaryMessage(args: {
  report: PublishedResearchReport
  displayStatus: SeoReportDisplayStatus
  readinessSummary: string
  isStale: boolean
  isEnriching: boolean
  hasComponentFailures: boolean
  hasUpgradeGates: boolean
}): string | null {
  const {
    report,
    displayStatus,
    readinessSummary,
    isStale,
    isEnriching,
    hasComponentFailures,
    hasUpgradeGates,
  } = args

  if (
    isStale &&
    (report.status === 'queued' || report.status === 'generating')
  ) {
    return getSeoReportStaleProcessingMessage(report.status)
  }

  if (displayStatus === 'failed') {
    const lastError = report.lastError?.trim()
    if (
      lastError?.startsWith('Failed:') ||
      lastError?.startsWith('Missing:') ||
      lastError === getSeoReportStaleProcessingMessage('queued') ||
      lastError === getSeoReportStaleProcessingMessage('generating')
    ) {
      return readinessSummary
    }
    return lastError ?? readinessSummary ?? 'Report generation failed.'
  }

  if (displayStatus === 'processing') {
    if (report.status === 'queued') {
      return 'Report generation is queued.'
    }
    if (
      readinessSummary === 'Report is fully generated and ready to publish.'
    ) {
      return 'Finalizing publication.'
    }
    if (
      readinessSummary &&
      readinessSummary !== 'Report is not ready to publish.' &&
      !readinessSummary.startsWith('Missing:')
    ) {
      return readinessSummary
    }
    return 'Report generation is in progress.'
  }

  if (displayStatus === 'live' && isEnriching && hasComponentFailures) {
    return 'Report is live. Some generated components are still being enriched and others failed.'
  }

  if (displayStatus === 'live' && isEnriching) {
    return 'Report is live. Generated components are still being enriched.'
  }

  if (displayStatus === 'live' && hasComponentFailures) {
    return 'Report is live, but one or more generated components failed.'
  }

  if (displayStatus === 'live' && hasUpgradeGates) {
    return 'Report is live. Some generated components can be regenerated to the latest format.'
  }

  if (displayStatus === 'draft' && report.status === 'ready') {
    return 'Report is ready to publish.'
  }

  if (displayStatus === 'archived') {
    return 'Report is archived.'
  }

  return null
}

function getReportDocumentPayload(report: PublishedResearchReport): unknown {
  return (
    report.document ??
    (report as PublishedResearchReport & { documentPayload?: unknown })
      .documentPayload
  )
}

export function deriveSeoAdminReportState(
  report: PublishedResearchReport,
  options?: { now?: number }
): SeoAdminReportState {
  const documentPayload = getReportDocumentPayload(report)
  const slugReady = report.slug.trim().length > 0
  const publicationReadiness = deriveSeoReportPublicationReadiness({
    status: report.status,
    visibility: report.visibility,
    updatedAt: report.updatedAt,
    documentPayload,
    categorySlug: report.categorySlug,
    generatedComponents: report.generatedComponents,
    now: options?.now,
  })
  const publicationReadinessSummary =
    summarizeSeoPublicationReadiness(publicationReadiness)
  const display = deriveSeoReportDisplayModel({
    status: report.status,
    visibility: report.visibility,
    updatedAt: report.updatedAt,
    documentPayload,
    categorySlug: report.categorySlug,
    generatedComponents: report.generatedComponents,
    now: options?.now,
  })

  const componentRecords = getGeneratedComponentRecords(
    report.generatedComponents
  )
  const componentStates = Object.fromEntries(
    SEO_ADMIN_COMPONENT_TARGETS.map((target) => [
      target,
      deriveComponentState({
        target,
        component: componentRecords[target],
        reportDisplayStatus: display.primary,
        needsUpgrade: publicationReadiness.upgradeGates.includes(
          target as SeoReportPublicationGate
        ),
        missingCurrentPayload: publicationReadiness.missingGates.includes(
          target as SeoReportPublicationGate
        ),
        updatedAt: report.updatedAt,
        now: options?.now,
      }),
    ])
  ) as Record<SeoAdminComponentTarget, SeoAdminComponentState>

  const anyComponentProcessing = Object.values(componentStates).some(
    (componentState) => componentState.isProcessing
  )
  const seoMetadataState = deriveSeoMetadataState({
    generatedComponents: report.generatedComponents,
    reportDisplayStatus: display.primary,
    upgradeGates: publicationReadiness.upgradeGates,
    updatedAt: report.updatedAt,
    now: options?.now,
  })
  const productImagesStatus = getEffectiveSeoGeneratedArtifactStatus({
    artifact: report.generatedProductImages,
    fallbackUpdatedAt: report.updatedAt,
    now: options?.now,
    staleWhenProcessingWithoutTimestamp: display.primary === 'live',
  })
  const productImagesProcessing =
    productImagesStatus === 'queued' || productImagesStatus === 'generating'
  const anyGeneratedArtifactProcessing =
    anyComponentProcessing ||
    seoMetadataState.isProcessing ||
    productImagesProcessing

  return {
    rawStatus: report.status,
    displayStatus: display.primary,
    publicationReadiness: slugReady
      ? publicationReadiness.state
      : 'needs_attention',
    publicationReadinessSummary: slugReady
      ? publicationReadinessSummary
      : 'Missing: Slug.',
    isStale: display.staleProcessing,
    openIssueCount: report.openIssueCount ?? 0,
    isLive: display.primary === 'live',
    slugReady,
    isEnriching: display.isEnriching,
    hasComponentFailures: display.hasComponentFailures,
    upgradeGates: publicationReadiness.upgradeGates,
    shouldPoll:
      display.primary === 'processing' || anyGeneratedArtifactProcessing,
    canPublish: display.primary === 'draft' || display.primary === 'failed',
    canUnpublish: display.primary === 'live',
    canRegenerateAll:
      (display.primary === 'live' ||
        display.primary === 'draft' ||
        display.primary === 'failed') &&
      !anyGeneratedArtifactProcessing,
    canRegenerateDocument:
      display.primary === 'draft' || display.primary === 'failed',
    canDelete:
      display.primary !== 'processing' && !anyGeneratedArtifactProcessing,
    primaryMessage: getPrimaryMessage({
      report,
      displayStatus: display.primary,
      readinessSummary: publicationReadinessSummary,
      isStale: display.staleProcessing,
      isEnriching: display.isEnriching,
      hasComponentFailures: display.hasComponentFailures,
      hasUpgradeGates: publicationReadiness.upgradeGates.length > 0,
    }),
    componentStates,
    seoMetadataState,
    publicationGateStates: derivePublicationGateStates({
      slugReady,
      processingGates: publicationReadiness.processingGates,
      failedGates: publicationReadiness.failedGates,
      missingGates: publicationReadiness.missingGates,
      upgradeGates: publicationReadiness.upgradeGates,
    }),
  }
}

export function getMissingSeoAdminComponentTargets(
  state: SeoAdminReportState
): SeoAdminComponentTarget[] {
  return SEO_ADMIN_COMPONENT_TARGETS.filter((target) => {
    const componentState = state.componentStates[target]
    return (
      (componentState.displayStatus === 'idle' ||
        componentState.displayStatus === 'failed' ||
        componentState.needsUpgrade) &&
      componentState.canRegenerate
    )
  })
}

export function shouldRegenerateSeoAdminMetadata(
  state: SeoAdminReportState
): boolean {
  return (
    (state.seoMetadataState.displayStatus === 'idle' ||
      state.seoMetadataState.displayStatus === 'failed' ||
      state.seoMetadataState.needsUpgrade) &&
    state.seoMetadataState.canRegenerate
  )
}
